import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { googleKeepApi, KeepSetupInput, KeepExportInput, KeepUpdateSettingsInput } from '../api/googleKeep';
import { useToast } from '../components/ui/Toast';
import { AxiosError } from 'axios';

export const googleKeepKeys = {
  all: ['googleKeep'] as const,
  status: () => [...googleKeepKeys.all, 'status'] as const,
};

export function useKeepStatus(enabled = false) {
  return useQuery({
    queryKey: googleKeepKeys.status(),
    queryFn: () => googleKeepApi.getStatus(),
    enabled,
  });
}

export function useKeepSetup() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: KeepSetupInput) => googleKeepApi.setup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: googleKeepKeys.all });
      showToast('Google Keep connected successfully', 'success');
    },
    onError: (error: AxiosError<{ error: string }>) => {
      const message = error.response?.data?.error || 'Failed to connect to Google Keep';
      showToast(message, 'error');
    },
  });
}

export function useKeepDisconnect() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: () => googleKeepApi.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: googleKeepKeys.all });
      showToast('Google Keep disconnected', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to disconnect Google Keep', 'error');
    },
  });
}

export function useKeepUpdateSettings() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: KeepUpdateSettingsInput) => googleKeepApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: googleKeepKeys.all });
      showToast('Google Keep settings updated', 'success');
    },
    onError: (error: AxiosError<{ error: string }>) => {
      const message = error.response?.data?.error || 'Failed to update settings';
      showToast(message, 'error');
    },
  });
}

export function useKeepExport() {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: KeepExportInput) => googleKeepApi.exportToKeep(data),
    onSuccess: data => {
      showToast(`"${data.note_title}" created in Google Keep with ${data.item_count} items`, 'success');
    },
    onError: (
      error: AxiosError<{
        error: string;
        errors?: Array<{ error_code: string }>;
      }>,
    ) => {
      const errorCode = error.response?.data?.errors?.[0]?.error_code;
      if (errorCode === 'KEEP_TOKEN_EXPIRED') {
        showToast('Google Keep session expired. Please reconnect your account.', 'error');
      } else {
        const message = error.response?.data?.error || 'Failed to export to Google Keep';
        showToast(message, 'error');
      }
    },
  });
}
